<script setup>
import { computed } from 'vue';

const props = defineProps({
  file: {
    type: Object,
    required: true,
  },
  size: {
    type: String,
    default: 'md', // 'sm', 'md', 'lg'
  },
  isFolder: {
    type: Boolean,
    default: false,
  },
});

const sizeClasses = computed(() => {
  const sizes = {
    sm: {
      container: 'w-10 h-10',
      icon: 'size-4',
      text: 'text-[8px]',
      fold: 'w-2 h-2',
    },
    md: {
      container: 'w-16 h-16',
      icon: 'size-6',
      text: 'text-[9px]',
      fold: 'w-3 h-3',
    },
    lg: {
      container: 'w-20 h-20',
      icon: 'size-10',
      text: 'text-[11px]',
      fold: 'w-4 h-4',
    },
  };
  return sizes[props.size] || sizes.md;
});

const iconConfig = computed(() => {
  if (props.isFolder) {
    return {
      bg: 'bg-gradient-to-b from-blue-400 to-blue-500',
      icon: 'i-lucide-folder',
      iconColor: 'text-white',
      label: '',
      isFolder: true,
    };
  }

  const { file_type, extension } = props.file;
  const ext = extension?.toUpperCase() || 'FILE';

  const configs = {
    image: {
      bg: 'bg-white',
      icon: 'i-lucide-image',
      iconColor: 'text-purple-500',
      label: ext === 'FILE' ? 'JPG' : ext,
      labelColor: 'text-purple-600',
    },
    video: {
      bg: 'bg-white',
      icon: 'i-lucide-video',
      iconColor: 'text-pink-500',
      label: ext === 'FILE' ? 'MP4' : ext,
      labelColor: 'text-pink-600',
    },
    pdf: {
      bg: 'bg-white',
      icon: 'i-lucide-file-text',
      iconColor: 'text-red-500',
      label: 'PDF',
      labelColor: 'text-red-600',
    },
    document: {
      bg: 'bg-white',
      icon: 'i-lucide-file-text',
      iconColor: 'text-blue-500',
      label: ext === 'FILE' ? 'DOC' : ext,
      labelColor: 'text-blue-600',
    },
    spreadsheet: {
      bg: 'bg-white',
      icon: 'i-lucide-table-2',
      iconColor: 'text-emerald-500',
      label: ext === 'FILE' ? 'XLS' : ext,
      labelColor: 'text-emerald-600',
    },
    archive: {
      bg: 'bg-white',
      icon: 'i-lucide-folder-archive',
      iconColor: 'text-amber-500',
      label: ext === 'FILE' ? 'ZIP' : ext,
      labelColor: 'text-amber-600',
    },
    code: {
      bg: 'bg-white',
      icon: 'i-lucide-code-2',
      iconColor: 'text-indigo-500',
      label: ext,
      labelColor: 'text-indigo-600',
    },
    text: {
      bg: 'bg-white',
      icon: 'i-lucide-file-text',
      iconColor: 'text-gray-500',
      label: 'TXT',
      labelColor: 'text-gray-600',
    },
    other: {
      bg: 'bg-white',
      icon: 'i-lucide-file',
      iconColor: 'text-gray-500',
      label: ext,
      labelColor: 'text-gray-600',
    },
    default: {
      bg: 'bg-white',
      icon: 'i-lucide-file',
      iconColor: 'text-gray-500',
      label: ext,
      labelColor: 'text-gray-600',
    },
  };

  return configs[file_type] || configs.default;
});
</script>

<template>
  <!-- Dossier style Finder -->
  <div
    v-if="isFolder"
    class="relative flex items-center justify-center rounded-xl transition-all duration-200 shadow-sm"
    :class="[sizeClasses.container, iconConfig.bg]"
  >
    <span :class="[iconConfig.icon, iconConfig.iconColor]" />
  </div>

  <!-- Fichier style Finder avec coin replié -->
  <div
    v-else
    class="relative flex flex-col items-center justify-center rounded-lg transition-all duration-200 shadow-sm"
    :class="[sizeClasses.container, iconConfig.bg]"
  >
    <!-- Coin replié (fold) -->
    <div
      class="absolute top-0 right-0 bg-gradient-to-bl from-gray-300 to-gray-200 rounded-bl-md finder-fold"
      :class="[sizeClasses.fold]"
    />

    <!-- Icône centrée -->
    <span :class="[iconConfig.icon, iconConfig.iconColor]" />

    <!-- Extension en bas -->
    <span
      v-if="iconConfig.label"
      class="mt-1 font-semibold uppercase tracking-tight"
      :class="[sizeClasses.text, iconConfig.labelColor]"
    >
      {{ iconConfig.label }}
    </span>
  </div>
</template>

<style scoped>
.finder-fold {
  clip-path: polygon(0 0, 100% 0, 100% 100%);
}
</style>
